import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const MAX_SNAPSHOTS = 10;

export interface ProjectSnapshot {
  id: string;
  project_id: string;
  user_id?: string;
  files_snapshot: Array<{ name: string; content: string }>;
  file_count?: number;
  prompt: string;
  label?: string | null;
  deploy_url?: string | null;
  created_at: string;
}

export function useProjectSnapshots(projectId: string | null) {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState<ProjectSnapshot[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load snapshots when projectId changes
  useEffect(() => {
    if (projectId) {
      loadSnapshots(projectId);
    } else {
      setSnapshots([]);
    }
  }, [projectId]);

  const loadSnapshots = useCallback(async (projId: string) => {
    setIsLoading(true);
    try {
      // Try new app_snapshots table first, fall back to project_snapshots
      let { data, error } = await (supabase as any)
        .from('app_snapshots')
        .select('*')
        .eq('project_id', projId)
        .order('created_at', { ascending: false })
        .limit(MAX_SNAPSHOTS);

      // If app_snapshots doesn't exist or has no data, fall back to project_snapshots
      if (error || !data || data.length === 0) {
        const fallback = await supabase
          .from('project_snapshots')
          .select('*')
          .eq('project_id', projId)
          .order('created_at', { ascending: false })
          .limit(MAX_SNAPSHOTS);

        if (!fallback.error) {
          data = fallback.data;
        }
      }

      if (!data) {
        setSnapshots([]);
        return;
      }

      setSnapshots((data || []).map((s: any) => {
        // Handle both formats: object {"path": "content"} and array [{name, content}]
        const rawFiles = s.files ?? s.files_snapshot;
        let filesArray: Array<{ name: string; content: string }>;

        if (Array.isArray(rawFiles)) {
          // Already an array (legacy project_snapshots format)
          filesArray = rawFiles;
        } else if (rawFiles && typeof rawFiles === 'object') {
          // Object format {"path": "content"} — convert to array
          filesArray = Object.entries(rawFiles).map(([name, content]) => ({
            name,
            content: content as string,
          }));
        } else {
          filesArray = [];
        }

        return {
          ...s,
          files_snapshot: filesArray,
          // Prefer stored file_count, fallback to computed length
          file_count: s.file_count ?? filesArray.length,
          prompt: s.prompt ?? '',
          label: s.label ?? null,
          deploy_url: s.deploy_url ?? null,
        };
      }));
    } catch (err) {
      console.error('Error loading snapshots:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const pruneToLimit = useCallback(async (projId: string, keepCount: number) => {
    try {
      // Prune from app_snapshots table
      const { data, error } = await (supabase as any)
        .from('app_snapshots')
        .select('id, created_at')
        .eq('project_id', projId)
        .order('created_at', { ascending: true });

      if (error || !data || data.length <= keepCount) return;

      const toDelete = data.slice(0, data.length - keepCount).map((r: any) => r.id);
      if (toDelete.length === 0) return;

      await (supabase as any).from('app_snapshots').delete().in('id', toDelete);
    } catch (err) {
      console.warn('Error pruning snapshots:', err);
    }
  }, []);

  const createSnapshot = useCallback(async (
    projId: string,
    files: Array<{ name: string; content: string }>,
    prompt: string,
    label?: string,
    deploy_url?: string
  ): Promise<boolean> => {
    const activeProjectId = projectId ?? projId;
    if (!activeProjectId || !user?.id) {
      console.warn('createSnapshot: project_id and user_id are required');
      return false;
    }
    try {
      // Auto-generate label like "Build 1", "Build 2" if not provided
      let finalLabel = label;
      if (!finalLabel || finalLabel === "After edit") {
        // Count existing snapshots to determine build number
        const { count } = await (supabase as any)
          .from('app_snapshots')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', activeProjectId);

        const buildNumber = (count ?? 0) + 1;
        finalLabel = `Build ${buildNumber}`;
      }

      // Convert files array to object format for jsonb storage
      const filesObj: Record<string, string> = {};
      for (const f of files) {
        filesObj[f.name] = f.content;
      }

      const payload = {
        project_id: activeProjectId,
        user_id: user.id,
        label: finalLabel,
        file_count: files.length,
        files: filesObj,
        deploy_url: deploy_url || null,
      };

      // Insert and return the created row to verify it was actually inserted
      // (RLS with missing WITH CHECK can silently filter inserts)
      const { data: insertedRow, error } = await (supabase as any)
        .from('app_snapshots')
        .insert(payload)
        .select('id, project_id, user_id, label')
        .single();

      if (error) {
        console.error('[useProjectSnapshots] Insert failed:', error.message, error.code, error.details);
        // Fall back to project_snapshots if app_snapshots fails
        const fallbackPayload = {
          project_id: activeProjectId,
          files: files,
          label: finalLabel,
        };
        const fallbackResult = await supabase
          .from('project_snapshots')
          .insert(fallbackPayload);
        if (fallbackResult.error) {
          console.error('[useProjectSnapshots] Fallback insert also failed:', fallbackResult.error);
          return false;
        }
        console.log('[useProjectSnapshots] Used fallback project_snapshots table');
      } else if (!insertedRow) {
        // Insert "succeeded" but no row returned — RLS silently filtered it
        console.error('[useProjectSnapshots] Insert returned no row — RLS may have blocked it. user_id:', user.id, 'project_id:', activeProjectId);
        return false;
      } else {
        console.log('[useProjectSnapshots] Snapshot created:', insertedRow.id, insertedRow.label);
      }

      await pruneToLimit(activeProjectId, MAX_SNAPSHOTS);
      await loadSnapshots(activeProjectId);
      return true;
    } catch (err) {
      console.error('Error creating snapshot:', err);
      return false;
    }
  }, [projectId, user?.id, loadSnapshots, pruneToLimit]);

  const deleteSnapshot = useCallback(async (snapshotId: string): Promise<boolean> => {
    try {
      // Try deleting from app_snapshots first
      let { error } = await (supabase as any)
        .from('app_snapshots')
        .delete()
        .eq('id', snapshotId);

      // Fall back to project_snapshots if needed
      if (error) {
        const fallback = await supabase
          .from('project_snapshots')
          .delete()
          .eq('id', snapshotId);
        error = fallback.error;
      }

      if (error) {
        console.error('Failed to delete snapshot:', error);
        return false;
      }

      setSnapshots(prev => prev.filter(s => s.id !== snapshotId));
      return true;
    } catch (err) {
      console.error('Error deleting snapshot:', err);
      return false;
    }
  }, []);

  return {
    snapshots,
    isLoading,
    loadSnapshots,
    createSnapshot,
    deleteSnapshot,
  };
}
